import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Download, Folder, File } from "lucide-react";
import { apiClient } from "../../utils/apiClient/index.ts";
import { useAuth } from "../../contexts/AuthContext";
import { downloadFromResponse } from "../../utils/fileDownload";

interface RecentFile {
  id: string;
  filename: string;
  mimeType: string;
  size: number;
  uploadedAt: string;
  caseTitle?: string;
}

interface AssignedCase {
  caseId: string;
  caseTitle: string;
  assignedAt: string;
}

export const ClientDashboard: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [recentFiles, setRecentFiles] = useState<RecentFile[]>([]);
  const [assignedCases, setAssignedCases] = useState<AssignedCase[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Get user's assigned cases
      const cases = await apiClient.getAssignedCases();
      setAssignedCases(
        cases.map((c) => ({
          caseId: c.id,
          caseTitle: c.title,
          assignedAt: c.createdAt,
        })),
      );

      // Get recent files from assigned cases
      // Note: This is a simplified version. Ideally we'd have a dedicated endpoint
      // for recent files accessible to the client
      const allFiles: RecentFile[] = [];
      for (const caseItem of cases.slice(0, 3)) {
        try {
          const files = await apiClient.listFilesByCase(caseItem.id);
          allFiles.push(
            ...files.slice(0, 5).map((f) => ({
              id: f.id,
              filename: f.filename,
              mimeType: f.mimeType,
              size: f.size,
              uploadedAt: f.uploadedAt,
              caseTitle: caseItem.title,
            })),
          );
        } catch (err) {
          console.error(`Failed to load files for case ${caseItem.id}:`, err);
        }
      }

      // Sort by upload date and take top 10
      allFiles.sort(
        (a, b) =>
          new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime(),
      );
      setRecentFiles(allFiles.slice(0, 10));
    } catch (error) {
      console.error("Failed to load dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith("image/")) return "🖼️";
    if (mimeType.includes("pdf")) return "📄";
    if (mimeType.includes("word")) return "📝";
    if (mimeType.includes("sheet") || mimeType.includes("excel")) return "📊";
    return "📎";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-gray-600 dark:text-gray-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
        <h1 className="mb-2 text-title-md2 font-semibold text-black dark:text-white">
          Welcome, {currentUser?.fullName || currentUser?.email}
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Access your case documents and shared files
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {/* Assigned Cases */}
        <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-950 flex items-center justify-center">
              <Folder className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-black dark:text-white">
                {assignedCases.length}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Assigned Cases
              </p>
            </div>
          </div>
          {assignedCases.length > 0 && (
            <button
              onClick={() => navigate("/portal/documents")}
              className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
            >
              View all documents →
            </button>
          )}
        </div>

        {/* Recent Files */}
        <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-green-100 dark:bg-green-950 flex items-center justify-center">
              <File className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-black dark:text-white">
                {recentFiles.length}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Recent Files
              </p>
            </div>
          </div>
          {recentFiles.length > 0 && (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Last updated {formatDate(recentFiles[0].uploadedAt)}
            </p>
          )}
        </div>
      </div>

      {/* Recent Files List */}
      {recentFiles.length > 0 && (
        <div className="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
          <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-800">
            <h2 className="text-lg font-semibold text-black dark:text-white">
              Recent Files
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Files you have access to
            </p>
          </div>

          <div className="divide-y divide-gray-200 dark:divide-gray-800">
            {recentFiles.map((file) => (
              <div
                key={file.id}
                className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer"
                onClick={() => navigate("/portal/documents")}
              >
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <span className="text-2xl">{getFileIcon(file.mimeType)}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-black dark:text-white truncate">
                      {file.filename}
                    </p>
                    {file.caseTitle && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {file.caseTitle}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-6 ml-4">
                  <div className="text-right">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {formatFileSize(file.size)}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                      {formatDate(file.uploadedAt)}
                    </p>
                  </div>
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        const response = await apiClient.downloadFileResponse(
                          file.id,
                        );
                        await downloadFromResponse(response, file.filename);
                      } catch (error) {
                        console.error("Download failed:", error);
                      }
                    }}
                    className="p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {recentFiles.length >= 10 && (
            <div className="border-t border-gray-200 px-6 py-4 dark:border-gray-800">
              <button
                onClick={() => navigate("/portal/documents")}
                className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                View all files →
              </button>
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {assignedCases.length === 0 && (
        <div className="rounded-2xl border border-gray-200 bg-white p-12 text-center dark:border-gray-800 dark:bg-white/[0.03]">
          <Folder className="w-16 h-16 mx-auto mb-4 text-gray-400 dark:text-gray-600" />
          <h3 className="text-lg font-semibold text-black dark:text-white mb-2">
            No Cases Assigned
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            You don't have any cases assigned yet. Contact your attorney for
            more information.
          </p>
        </div>
      )}
    </div>
  );
};
